import styles from "./styles.module.scss";
import dayjs from "dayjs";
import RelativeTime from "dayjs/plugin/relativeTime";
import classnames from "classnames";
import { useQuery } from "@tanstack/react-query";
import { FlexListSummary, ListSummary } from "../../components/listSummary";
import { getSummary, getTableInsightsById, getTableStorageOverTimeById } from "../../helpers/apis";
import { ComponentLoader, RelativeComponentLoader } from "../../components/loader";
import { formatNumber } from "@/uiCore";
import { useAppState } from "@/modules/app/useAppContext";
import { formatCost, formatDate } from "@/helpers/utils";
import type { SummaryItem } from "../../components/listSummary/types";

dayjs.extend(RelativeTime);

// ListSummary component's actual item type (independent of the shared SummaryItem type).
interface ListSummaryItem {
  label: string;
  value: number;
  info?: string;
  color?: string;
}

const DYNAMIC_TABLE_ATTRIBUTES = ["refresh_mode", "target_lag", "scheduling_state"];

const GET_SUMMARY = "GET-SUMMARY";
const GET_TABLE_INSIGHTS_BY_ID = "GET-TABLE_INSIGHTS-BY-ID";
const GET_TABLE_STORAGE_OVER_TIME_BY_ID = "GET-STORAGE_OVER_TIME-BY-ID";

const ANNUALIZED_STORAGE_TOOLTIP =
  "(ACTIVE_BYTES + TIME_TRAVEL_BYTES + FAILSAFE_BYTES + RETAINED_FOR_CLONE_BYTES) / (1024 ^4) * 23 * 12";

interface SummaryProps {
  table?: string;
}

interface TableInsightsSummaryProps {
  tableId: string;
}

interface TableStorageSummaryProps {
  tableId: string;
}

const Summary = ({ table }: SummaryProps) => {
  const { data, isLoading } = useQuery({
    queryKey: [GET_SUMMARY],
    queryFn: () => getSummary(table ? { table_id: table } : {}),
  });
  const lastIncidentDaysAgo = data?.last_violation_timestamp
    ? dayjs().diff(dayjs(data.last_violation_timestamp), "day")
    : 0;
  const items: ListSummaryItem[] = [
    {
      label: "Last Incident",
      value: lastIncidentDaysAgo,
      info: data?.last_violation_timestamp
        ? `${dayjs(data.last_violation_timestamp).fromNow(true)} ago`
        : "None",
    },
    { label: "Errors", value: data?.total_error_count ?? 0 },
    { label: "Warning", value: data?.total_warning_count ?? 0 },
    {
      label: "Policies",
      value: data?.total_policy_violation_count ?? 0,
    },
    { label: "Validations", value: data?.total_validation_count ?? 0 },
  ];
  if (isLoading) {
    return <ComponentLoader top={25} left={60} label="Loading summary..." />;
  }
  return <ListSummary summary={items} />;
};

const TableInsightsSummary = ({ tableId }: TableInsightsSummaryProps) => {
  const { currency } = useAppState();
  const { data: tableInsights, isLoading } = useQuery({
    queryKey: [GET_TABLE_INSIGHTS_BY_ID, tableId],
    queryFn: () => {
      return getTableInsightsById(tableId);
    },
  });
  if (!tableInsights) {
    return null;
  }

  const items: SummaryItem[] = tableInsights?.is_dynamic_table
    ? [
        {
          label: "Annualized Storage Spend",
          info: ANNUALIZED_STORAGE_TOOLTIP,
          value: formatCost(tableInsights.insights.estimated_annualized_storage_costs ?? 0, currency),
        },
        {
          label: "Annual Data Refresh Cost",
          value: formatCost(tableInsights.insights.annualized_refresh_cost ?? 0, currency) || "None",
        },
        {
          label: "Last Refreshed",
          value: formatDate(tableInsights.insights.last_modified ?? ""),
        },
        {
          label: "Last Read",
          value: formatDate(tableInsights.insights.last_read ?? "") || "N/A",
        },
        {
          label: "Refreshes\n(in last month)",
          value: tableInsights.insights.refresh_count != null ? String(tableInsights.insights.refresh_count) : "None",
        },
      ]
    : [
        {
          label: "Annualized Storage Spend",
          info: ANNUALIZED_STORAGE_TOOLTIP,
          value: formatCost(tableInsights.insights.estimated_annualized_storage_costs ?? 0, currency),
        },
        {
          label: "Annual Data Insertion Cost",
          value: formatCost(tableInsights.insights.annualized_insert_cost ?? 0, currency) || "None",
        },
        {
          label: "Last Write",
          value: formatDate(tableInsights.insights.last_modified ?? ""),
        },
        {
          label: "Last Read",
          value: formatDate(tableInsights.insights.last_read ?? ""),
        },
        {
          label: "Reads/Writes \n(in last month)",
          value: tableInsights.insights.read_write_ratio || "None",
        },
      ];
  if (isLoading) {
    return <RelativeComponentLoader componentHeight={20} />;
  }
  return <FlexListSummary summary={items || []} />;
};

const TableStorageSummary = ({ tableId }: TableStorageSummaryProps) => {
  const { currency } = useAppState();
  const { data: tableStorageOverTime, isLoading } = useQuery({
    queryKey: [GET_TABLE_STORAGE_OVER_TIME_BY_ID, tableId],
    queryFn: () => {
      return getTableStorageOverTimeById(tableId);
    },
  });
  if (isLoading) {
    return <RelativeComponentLoader componentHeight={20} />;
  }
  if (
    !tableStorageOverTime?.table_owner ||
    tableStorageOverTime["table_owner"] === "Owner not found"
  ) {
    return null;
  }

  const dynamicAttrs = tableStorageOverTime.dynamic_table_attributes ?? {};
  const storageEntries = Object.entries(
    tableStorageOverTime.is_dynamic_table
      ? { ...dynamicAttrs, ...tableStorageOverTime }
      : tableStorageOverTime
  );

  return (
    <div className={styles.summary_card}>
      {storageEntries.map(([key, value]) =>
        value !== null && key !== "dynamic_table_attributes" && key !== "is_dynamic_table" ? (
          <p key={key}>
            <span className={classnames("text-muted", styles.summary_text)}>{key} :</span>
            <span>
              {tableStorageOverTime.is_dynamic_table && DYNAMIC_TABLE_ATTRIBUTES.includes(key)
                ? dynamicAttrs[key]
                : key === "cluster_cost" && (typeof value === "string" || typeof value === "number")
                  ? formatNumber(value, { currency })
                  : value}
            </span>
            {"   "}
          </p>
        ) : null
      )}
    </div>
  );
};

export { Summary, TableInsightsSummary, TableStorageSummary };
